/**
 * Metric Definition Editor Component
 * Modal for creating and editing custom GEval metrics
 */

import { useState, useEffect } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useI18n } from '@/i18n';
import { useCreateMetricDefinition, useUpdateMetricDefinition, useGenerateMetric } from '../hooks/useEvalMetrics';
import { useAgentTypes } from '../hooks/useEvalScenarios';
import { useChatModelsQuery } from '@/lib/queries/useModelsQuery';
import type { MetricDefinition, CreateMetricDefinitionRequest, UpdateMetricDefinitionRequest } from '@/types/evalScenarios';

interface MetricDefinitionEditorProps {
  metric: MetricDefinition | null; // null for create mode
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function MetricDefinitionEditor({ metric, isOpen, onClose, onSuccess }: MetricDefinitionEditorProps) {
  const { messages, language } = useI18n();
  const t = messages.evalScenarios.metricDefinitions;
  const te = t?.editor;
  const tc = messages.common;

  const isEditMode = !!metric;

  const createMutation = useCreateMetricDefinition();
  const updateMutation = useUpdateMetricDefinition();
  const generateMutation = useGenerateMetric();
  const { data: agentTypes = [] } = useAgentTypes();
  const { data: chatModels = [] } = useChatModelsQuery();

  // Form state
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultThreshold, setDefaultThreshold] = useState(0.7);
  const [thresholdDirection, setThresholdDirection] = useState<'higher' | 'lower'>('higher');
  const [criteria, setCriteria] = useState('');
  const [evaluationSteps, setEvaluationSteps] = useState<string[]>([]);
  const [applicableAgentTypes, setApplicableAgentTypes] = useState<string[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // AI Generation state
  const [aiDescription, setAiDescription] = useState('');
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiExplanation, setAiExplanation] = useState('');
  const [selectedModel, setSelectedModel] = useState('');

  // Reset form when metric changes
  useEffect(() => {
    if (metric) {
      setName(metric.name);
      setDisplayName(metric.display_name);
      setDescription(metric.description || '');
      setDefaultThreshold(metric.default_threshold);
      setThresholdDirection(metric.threshold_direction);
      setCriteria(metric.criteria || '');
      setEvaluationSteps(metric.evaluation_steps || []);
      setApplicableAgentTypes(metric.applicable_agent_types);
      setShowAdvanced(!!metric.evaluation_steps?.length);
    } else {
      setName('');
      setDisplayName('');
      setDescription('');
      setDefaultThreshold(0.7);
      setThresholdDirection('higher');
      setCriteria('');
      setEvaluationSteps([]);
      setApplicableAgentTypes([]);
      setShowAdvanced(false);
    }
    // Reset AI state
    setAiDescription('');
    setShowAiPanel(false);
    setAiExplanation('');
    setSelectedModel('');
  }, [metric, isOpen]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleAddStep = () => {
    setEvaluationSteps([...evaluationSteps, '']);
  };

  const handleRemoveStep = (index: number) => {
    setEvaluationSteps(evaluationSteps.filter((_, i) => i !== index));
  };

  const handleStepChange = (index: number, value: string) => {
    const newSteps = [...evaluationSteps];
    newSteps[index] = value;
    setEvaluationSteps(newSteps);
  };

  const handleAgentTypeToggle = (agentType: string) => {
    if (applicableAgentTypes.includes(agentType)) {
      setApplicableAgentTypes(applicableAgentTypes.filter((t) => t !== agentType));
    } else {
      setApplicableAgentTypes([...applicableAgentTypes, agentType]);
    }
  };

  const handleAiGenerate = async () => {
    if (!aiDescription.trim() || aiDescription.length < 10) {
      alert(te?.aiDescriptionTooShort || 'Please provide a more detailed description (at least 10 characters)');
      return;
    }
    if (!selectedModel) {
      alert(te?.selectModelFirst || 'Please select a model first');
      return;
    }

    try {
      const result = await generateMutation.mutateAsync({
        description: aiDescription,
        agent_type: applicableAgentTypes.length > 0 ? applicableAgentTypes[0] : undefined,
        language: language,
        model: selectedModel,
      });

      // Fill form with generated data
      if (!isEditMode) {
        setName(result.suggested_name);
      }
      setDisplayName(result.suggested_display_name);
      setCriteria(result.criteria);
      setEvaluationSteps(result.evaluation_steps);
      setDefaultThreshold(result.suggested_threshold);
      setThresholdDirection(result.threshold_direction);
      setShowAdvanced(result.evaluation_steps.length > 0);
      setAiExplanation(result.explanation);
    } catch (err) {
      console.error('AI generation failed:', err);
      alert(te?.aiGenerationFailed || 'AI generation failed. Please try again.');
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!name.trim()) {
      alert('Name is required');
      return;
    }
    if (!displayName.trim()) {
      alert('Display name is required');
      return;
    }
    if (!criteria.trim() && evaluationSteps.filter((s) => s.trim()).length === 0) {
      alert('Either criteria or evaluation steps is required');
      return;
    }

    const filteredSteps = evaluationSteps.filter((s) => s.trim());

    try {
      if (isEditMode) {
        const updateData: UpdateMetricDefinitionRequest = {
          display_name: displayName,
          description: description || undefined,
          criteria: criteria || undefined,
          evaluation_steps: filteredSteps.length > 0 ? filteredSteps : undefined,
          default_threshold: defaultThreshold,
          threshold_direction: thresholdDirection,
          applicable_agent_types: applicableAgentTypes,
        };
        await updateMutation.mutateAsync({ name: metric!.name, data: updateData });
      } else {
        const createData: CreateMetricDefinitionRequest = {
          name,
          display_name: displayName,
          description: description || undefined,
          metric_type: 'geval',
          criteria: criteria || undefined,
          evaluation_steps: filteredSteps.length > 0 ? filteredSteps : undefined,
          default_threshold: defaultThreshold,
          threshold_direction: thresholdDirection,
          applicable_agent_types: applicableAgentTypes,
        };
        await createMutation.mutateAsync(createData);
      }
      onSuccess();
    } catch (err) {
      console.error('Failed to save metric:', err);
      alert('Failed to save metric: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <span className="text-2xl">✏️</span>
              <h2 className="text-lg font-medium text-gray-900">
                {isEditMode
                  ? te?.editTitle || 'Edit Metric'
                  : te?.createTitle || 'Create Custom Metric'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <span className="text-xl text-gray-500">×</span>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* AI Generation Panel */}
            <div className="border border-purple-200 rounded-lg bg-gradient-to-r from-purple-50 to-indigo-50">
              <button
                type="button"
                onClick={() => setShowAiPanel(!showAiPanel)}
                className="flex items-center gap-2 w-full px-4 py-3 text-left"
              >
                <Sparkles className="w-5 h-5 text-purple-500" />
                <span className="flex-1 font-medium text-purple-800">
                  {te?.aiAssistantTitle || 'AI Smart Design'}
                </span>
                <span className="text-xs text-purple-500">
                  {showAiPanel ? '▼' : '▶'}
                </span>
              </button>

              {showAiPanel && (
                <div className="px-4 pb-4 space-y-3">
                  <p className="text-sm text-purple-700">
                    {te?.aiAssistantDescription || 'Describe what you want to evaluate, AI will generate metric config based on DeepEval best practices'}
                  </p>

                  {/* Model Selector */}
                  <div>
                    <label className="block text-xs font-medium text-purple-700 mb-1">
                      {te?.selectModel || 'Select Model'}
                    </label>
                    <select
                      value={selectedModel}
                      onChange={(e) => setSelectedModel(e.target.value)}
                      className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-white"
                    >
                      <option value="">{te?.selectModelPlaceholder || '-- Select a model --'}</option>
                      {chatModels.map((model) => (
                        <option key={model.id} value={model.id}>
                          {model.id}
                        </option>
                      ))}
                    </select>
                  </div>

                  <textarea
                    value={aiDescription}
                    onChange={(e) => setAiDescription(e.target.value)}
                    placeholder={te?.aiDescriptionPlaceholder || 'e.g., Evaluate the accuracy of astrological interpretations in the response, including zodiac positions, planetary aspects, house interpretations...'}
                    rows={3}
                    className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-white"
                  />
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={handleAiGenerate}
                      disabled={generateMutation.isPending || aiDescription.length < 10 || !selectedModel}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      {generateMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          {te?.generating || 'Generating...'}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          {te?.generateMetric || 'Generate Config'}
                        </>
                      )}
                    </Button>
                    <span className="text-xs text-purple-500">
                      {aiDescription.length < 10
                        ? te?.minChars || 'At least 10 chars'
                        : `${aiDescription.length} ${te?.chars || 'chars'}`}
                    </span>
                  </div>

                  {/* AI Explanation */}
                  {aiExplanation && (
                    <div className="mt-3 p-3 bg-white border border-purple-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <span className="text-purple-500">✨</span>
                        <div>
                          <p className="text-xs font-medium text-purple-800 mb-1">
                            {te?.aiExplanation || 'AI Design Notes'}
                          </p>
                          <p className="text-sm text-gray-700">{aiExplanation}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t?.metricName || 'Metric Name'} *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                  disabled={isEditMode}
                  placeholder="e.g., custom_accuracy"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {te?.nameHint || 'Unique identifier, use lowercase letters and underscores'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t?.displayName || 'Display Name'} *
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="e.g., Custom Accuracy"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {te?.displayNameHint || 'Name displayed in the UI'}
                </p>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {tc.description}
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this metric evaluates..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Threshold */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t?.defaultThreshold || 'Default Threshold'}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={defaultThreshold}
                    onChange={(e) => setDefaultThreshold(parseFloat(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-sm font-medium w-14 text-right">
                    {(defaultThreshold * 100).toFixed(0)}%
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t?.thresholdDirection || 'Threshold Direction'}
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="thresholdDirection"
                      checked={thresholdDirection === 'higher'}
                      onChange={() => setThresholdDirection('higher')}
                      className="text-primary-600"
                    />
                    <span className="text-sm">{t?.higherBetter || 'Higher is better (≥)'}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="thresholdDirection"
                      checked={thresholdDirection === 'lower'}
                      onChange={() => setThresholdDirection('lower')}
                      className="text-primary-600"
                    />
                    <span className="text-sm">{t?.lowerBetter || 'Lower is better (≤)'}</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Applicable Agents */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t?.applicableAgents || 'Applicable Agents'}
                <span className="text-gray-400 font-normal ml-2">
                  ({applicableAgentTypes.length === 0 ? t?.allAgents || 'All' : applicableAgentTypes.length + ' selected'})
                </span>
              </label>
              <div className="flex flex-wrap gap-2">
                {agentTypes.map((agent) => (
                  <label
                    key={agent.value}
                    className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg cursor-pointer transition-colors ${
                      applicableAgentTypes.includes(agent.value)
                        ? 'bg-primary-50 border-primary-300 text-primary-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={applicableAgentTypes.includes(agent.value)}
                      onChange={() => handleAgentTypeToggle(agent.value)}
                      className="sr-only"
                    />
                    <span className="text-sm">{agent.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Leave empty for all agents
              </p>
            </div>

            {/* Criteria */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t?.criteria || 'Evaluation Criteria'} *
              </label>
              <textarea
                value={criteria}
                onChange={(e) => setCriteria(e.target.value)}
                placeholder={t?.criteriaPlaceholder || 'Describe how to evaluate response quality...'}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t?.criteriaHint || 'Criteria is passed to the LLM model to judge response quality.'}
              </p>
            </div>

            {/* Advanced: Evaluation Steps */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 w-full"
              >
                <span>{showAdvanced ? '▼' : '▶'}</span>
                <span className="flex-1 text-left">
                  {te?.evaluationStepsTitle || 'Evaluation Steps (Advanced)'}
                </span>
                <span className="text-xs text-gray-400 font-normal">
                  {te?.optional || 'Optional'}
                </span>
              </button>

              {showAdvanced && (
                <div className="mt-4 space-y-4">
                  {/* Explanation Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <span className="text-blue-500">💡</span>
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">
                          {te?.stepsVsCriteriaTitle || 'Evaluation Steps vs Criteria'}
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-xs">
                          <li>{te?.stepsExplanation1 || 'Criteria: Descriptive requirements, suitable for most scenarios'}</li>
                          <li>{te?.stepsExplanation2 || 'Steps: Step-by-step instructions, LLM executes each step in order'}</li>
                          <li>{te?.stepsExplanation3 || 'Steps override Criteria if both are set'}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Example Box */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs font-medium text-amber-800 mb-2">
                      {te?.exampleTitle || 'Example - Astrological Accuracy Steps:'}
                    </p>
                    <ol className="text-xs text-amber-700 space-y-1 list-decimal list-inside">
                      <li>{te?.exampleStep1 || 'Check if zodiac and planet positions match user chart data'}</li>
                      <li>{te?.exampleStep2 || 'Verify aspect interpretations follow astrological principles'}</li>
                      <li>{te?.exampleStep3 || 'Evaluate terminology for professionalism and accuracy'}</li>
                      <li>{te?.exampleStep4 || 'Based on above checks, give a score of 1-5'}</li>
                    </ol>
                  </div>

                  {/* Steps Input */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {te?.yourSteps || 'Your Evaluation Steps'}
                    </label>

                    {evaluationSteps.length === 0 && (
                      <p className="text-xs text-gray-500 italic">
                        {te?.noStepsYet || 'No steps yet. Click button below to add.'}
                      </p>
                    )}

                    {evaluationSteps.map((step, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <span className="text-sm text-gray-500 mt-2 w-6 flex-shrink-0">
                          {index + 1}.
                        </span>
                        <input
                          type="text"
                          value={step}
                          onChange={(e) => handleStepChange(index, e.target.value)}
                          placeholder={te?.stepPlaceholder || 'Describe what this step checks or evaluates...'}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white"
                        />
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleRemoveStep(index)}
                        >
                          {te?.removeStep || 'Remove'}
                        </Button>
                      </div>
                    ))}

                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleAddStep}
                      className="mt-2"
                    >
                      + {te?.addStep || 'Add Step'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose} disabled={isPending}>
              {tc.cancel}
            </Button>
            <Button onClick={handleSubmit} disabled={isPending}>
              {isPending ? tc.saving : tc.save}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
